import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ClipboardList, 
  Users, 
  DollarSign, 
  AlertTriangle, 
  FileText,
  ChevronRight,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    jobs: 0,
    pendingTimesheets: 0,
    totalPoValue: 0,
    crewMembers: 0,
    jobsNearPo: 0,
    jobsOverPo: 0,
    assignedJobs: 0
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const [summaryRes, timesheetsRes, crewRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/timesheets?status=SUBMITTED`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crew-members`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const summary = summaryRes.data;
      const pendingTimesheets = timesheetsRes.data;
      const crewMembers = crewRes.data;

      setStats({
        jobs: summary.jobCount || 0,
        pendingTimesheets: pendingTimesheets.length || 0,
        totalPoValue: summary.totalPoValue || 0,
        crewMembers: crewMembers.filter(c => c.active).length || 0,
        jobsNearPo: summary.jobsNearPo || 0,
        jobsOverPo: summary.jobsOverPo || 0,
        assignedJobs: summary.jobs?.length || 0
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const getRoleSpecificContent = () => {
    if (loading) {
      return (
        <div className='text-center text-slate-600 p-8'>Loading dashboard...</div>
      );
    }

    switch (user?.role) {
      case 'FOREMAN':
        return (
          <div className='space-y-4 md:space-y-6' data-testid='foreman-dashboard'>
            <div>
              <h1 className='text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900'>Welcome, {user.name}</h1>
              <p className='mt-1 md:mt-2 text-base md:text-lg text-slate-700'>Manage your crew timesheets and assigned jobs</p>
            </div>
            
            <div className='grid grid-cols-2 gap-3 md:gap-6'>
              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/jobs')}
              >
                <CardContent className='p-4 md:p-6'>
                  <div className='flex items-center gap-2 mb-2'>
                    <ClipboardList className='w-5 h-5 md:w-6 md:h-6 text-[#1A3668]' />
                    <span className='text-sm md:text-base font-bold text-slate-800'>My Jobs</span>
                  </div>
                  <div className='text-2xl md:text-3xl font-bold text-[#1A3668]'>{stats.assignedJobs}</div>
                  <p className='text-xs md:text-sm text-slate-500'>Active assignments</p>
                </CardContent>
              </Card>

              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/timesheets')}
              >
                <CardContent className='p-4 md:p-6'>
                  <div className='flex items-center gap-2 mb-2'>
                    <Users className='w-5 h-5 md:w-6 md:h-6 text-[#1A3668]' />
                    <span className='text-sm md:text-base font-bold text-slate-800'>Timesheets</span>
                  </div>
                  <div className='text-2xl md:text-3xl font-bold text-[#1A3668]'>{stats.pendingTimesheets}</div>
                  <p className='text-xs md:text-sm text-slate-500'>Pending</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions - Mobile Optimized */}
            <div className='space-y-3'>
              <Button 
                onClick={() => navigate('/timesheets/new')}
                className='w-full bg-[#1A3668] hover:bg-[#142A52] active:scale-[0.98] text-white font-bold rounded-sm py-4 md:py-3 text-base md:text-sm'
                data-testid='quick-new-timesheet'
              >
                <ClipboardList className='w-5 h-5 mr-2' />
                New Timesheet
              </Button>
              <Button 
                onClick={() => navigate('/dashboard/recap')}
                variant='outline'
                className='w-full border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 active:scale-[0.98] rounded-sm py-4 md:py-3 text-base md:text-sm'
              >
                <BarChart3 className='w-5 h-5 mr-2' />
                View Job Recap
              </Button>
            </div>
          </div>
        );

      case 'OFFICE':
        return (
          <div className='space-y-4 md:space-y-6' data-testid='office-dashboard'>
            <div>
              <h1 className='text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900'>Office Dashboard</h1>
              <p className='mt-1 md:mt-2 text-base md:text-lg text-slate-700'>Manage company operations and approvals</p>
            </div>
            
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4'>
              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/jobs')}
              >
                <CardContent className='p-4'>
                  <p className='text-xs font-bold uppercase tracking-wider text-slate-500'>Total Jobs</p>
                  <div className='text-2xl md:text-3xl font-bold text-[#1A3668] mt-1'>{stats.jobs}</div>
                  <p className='text-xs text-slate-500 mt-1'>Active projects</p>
                </CardContent>
              </Card>

              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/timesheets')}
              >
                <CardContent className='p-4'>
                  <p className='text-xs font-bold uppercase tracking-wider text-slate-500'>Pending Timesheets</p>
                  <div className='text-2xl md:text-3xl font-bold text-[#F59E0B] mt-1'>{stats.pendingTimesheets}</div>
                  <p className='text-xs text-slate-500 mt-1'>Awaiting approval</p>
                </CardContent>
              </Card>

              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/dashboard/recap')}
              >
                <CardContent className='p-4'>
                  <p className='text-xs font-bold uppercase tracking-wider text-slate-500'>Total PO Value</p>
                  <div className='text-2xl md:text-3xl font-bold text-[#16A34A] mt-1'>{formatCurrency(stats.totalPoValue)}</div>
                  <p className='text-xs text-slate-500 mt-1'>Across all jobs</p>
                </CardContent>
              </Card>

              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all rounded-sm cursor-pointer'
                onClick={() => navigate('/crew')}
              >
                <CardContent className='p-4'>
                  <p className='text-xs font-bold uppercase tracking-wider text-slate-500'>Crew Members</p>
                  <div className='text-2xl md:text-3xl font-bold text-[#1A3668] mt-1'>{stats.crewMembers}</div>
                  <p className='text-xs text-slate-500 mt-1'>Active workforce</p>
                </CardContent>
              </Card>
            </div>

            {/* Alerts - Mobile Optimized */}
            {(stats.jobsNearPo > 0 || stats.jobsOverPo > 0) && (
              <Card className='border-amber-200 bg-amber-50 shadow-sm rounded-sm'>
                <CardContent className='p-4 md:p-6'>
                  <div className='flex flex-col md:flex-row md:items-center gap-3'>
                    <div className='flex items-center gap-3'>
                      <AlertTriangle className='w-6 h-6 text-amber-600 flex-shrink-0' />
                      <div>
                        <h3 className='text-base md:text-lg font-bold text-amber-800'>Budget Alerts</h3>
                        <div className='flex flex-wrap gap-2 mt-1'>
                          {stats.jobsNearPo > 0 && (
                            <Badge className='bg-amber-100 text-amber-800 border-amber-300 text-xs'>
                              {stats.jobsNearPo} near limit
                            </Badge>
                          )}
                          {stats.jobsOverPo > 0 && (
                            <Badge className='bg-red-100 text-red-800 border-red-300 text-xs'>
                              {stats.jobsOverPo} over budget
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/dashboard/recap')}
                      variant='outline'
                      className='md:ml-auto border-amber-300 text-amber-800 hover:bg-amber-100 active:scale-[0.98] rounded-sm w-full md:w-auto py-3'
                    >
                      View Details
                      <ChevronRight className='w-4 h-4 ml-1' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions - Mobile Grid */}
            <div className='grid grid-cols-2 gap-3'>
              <Button 
                onClick={() => navigate('/dashboard/recap')}
                className='h-auto py-5 md:py-4 flex-col gap-2 bg-[#1A3668] hover:bg-[#142A52] active:scale-[0.98] text-white font-bold rounded-sm'
              >
                <BarChart3 className='w-7 h-7 md:w-6 md:h-6' />
                <span className='text-sm'>Job Recap</span>
              </Button>
              <Button 
                onClick={() => navigate('/invoices')}
                variant='outline'
                className='h-auto py-5 md:py-4 flex-col gap-2 border-2 border-slate-200 hover:border-[#1A3668] active:scale-[0.98] rounded-sm'
              >
                <FileText className='w-7 h-7 md:w-6 md:h-6' />
                <span className='text-sm'>Invoices</span>
              </Button>
              <Button 
                onClick={() => navigate('/timesheets')}
                variant='outline'
                className='h-auto py-5 md:py-4 flex-col gap-2 border-2 border-slate-200 hover:border-[#1A3668] active:scale-[0.98] rounded-sm'
              >
                <Users className='w-7 h-7 md:w-6 md:h-6' />
                <span className='text-sm'>Timesheets</span>
              </Button>
              <Button 
                onClick={() => navigate('/jobs')}
                variant='outline'
                className='h-auto py-5 md:py-4 flex-col gap-2 border-2 border-slate-200 hover:border-[#1A3668] active:scale-[0.98] rounded-sm'
              >
                <ClipboardList className='w-7 h-7 md:w-6 md:h-6' />
                <span className='text-sm'>Jobs</span>
              </Button>
            </div>
          </div>
        );

      case 'OWNER':
        return (
          <div className='space-y-4 md:space-y-6' data-testid='owner-dashboard'>
            <div>
              <h1 className='text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900'>Portfolio Overview</h1>
              <p className='mt-2 text-lg text-slate-700'>High-level insights across all projects</p>
            </div>
            
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md transition-shadow rounded-sm cursor-pointer'
                onClick={() => navigate('/dashboard/recap')}
              >
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-xl font-bold text-slate-800'>
                    <DollarSign className='w-5 h-5 text-[#16A34A]' />
                    Total PO Value
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='text-4xl font-bold text-[#16A34A]'>{formatCurrency(stats.totalPoValue)}</div>
                  <p className='text-sm text-slate-500 mt-1'>Total PO value</p>
                </CardContent>
              </Card>

              <Card 
                className='border-slate-200 shadow-sm hover:shadow-md transition-shadow rounded-sm cursor-pointer'
                onClick={() => navigate('/jobs')}
              >
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-xl font-bold text-slate-800'>
                    <ClipboardList className='w-5 h-5 text-[#1A3668]' />
                    Active Jobs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='text-4xl font-bold text-[#1A3668]'>{stats.jobs}</div>
                  <p className='text-sm text-slate-500 mt-1'>In progress</p>
                </CardContent>
              </Card>

              <Card className='border-slate-200 shadow-sm hover:shadow-md transition-shadow rounded-sm'>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-xl font-bold text-slate-800'>
                    <AlertTriangle className='w-5 h-5 text-[#F59E0B]' />
                    Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='text-4xl font-bold text-[#F59E0B]'>
                    {stats.jobsNearPo + stats.jobsOverPo}
                  </div>
                  <p className='text-sm text-slate-500 mt-1'>Budget warnings</p>
                </CardContent>
              </Card>
            </div>

            {/* View Detailed Recap Button */}
            <Card className='border-slate-200 shadow-sm rounded-sm'>
              <CardContent className='p-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h3 className='text-lg font-bold text-slate-800'>Detailed Job Recap</h3>
                    <p className='text-sm text-slate-500'>View labor, material, and PO breakdowns</p>
                  </div>
                  <Button 
                    onClick={() => navigate('/dashboard/recap')}
                    className='bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm'
                  >
                    <BarChart3 className='w-4 h-4 mr-2' />
                    View Job Recap
                    <ChevronRight className='w-4 h-4 ml-1' />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className='p-4 md:p-8'>
      {getRoleSpecificContent()}
    </div>
  );
}
